// Account-menu help dialog: shortcuts, quickbar syntax, optional intro restart.
import { useCallback, useId, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '@/features/auth/useAuth';
import { useT } from '@/i18n/useT';
import { unmarkHintSeen } from '@/services/userService';
import {
  IS_ONBOARDING_WELCOME_ROUTE_ENABLED,
  getOnboardingWelcomePath,
} from '@/constants/onboardingRoutes';
import { HelpShortcutsSection } from '@/components/layout/helpPanelDialogSections';
import {
  renderHelpQuickbarSection,
  renderHelpRestartSection,
} from '@/components/layout/helpPanelDialogRenderSections';
/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   returnFocusRef?: import('react').RefObject<HTMLElement | null>,
 * }} props
 */
export default function HelpPanelDialog({ open, onClose, returnFocusRef }) {
  const t = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const titleId = useId();
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);

  const restoreFocus = useCallback(() => {
    requestAnimationFrame(() => returnFocusRef?.current?.focus());
  }, [returnFocusRef]);

  const handleClose = useCallback(() => {
    onClose();
    restoreFocus();
  }, [onClose, restoreFocus]);

  const handleRestartRequest = useCallback(() => {
    setRestartConfirmOpen(true);
  }, []);

  const handleRestartCancel = useCallback(() => {
    setRestartConfirmOpen(false);
  }, []);

  const handleRestartConfirm = useCallback(async () => {
    if (user?.uid) {
      await unmarkHintSeen(user.uid, 'onboarding');
    }
    setRestartConfirmOpen(false);
    onClose();
    navigate(getOnboardingWelcomePath(), { state: { from: pathname } });
    restoreFocus();
  }, [user?.uid, onClose, navigate, pathname, restoreFocus]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby={titleId}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle id={titleId} sx={{ fontWeight: 700, pr: 6 }}>
          {t('help.title')}
          <IconButton
            aria-label={t('help.close')}
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {renderHelpQuickbarSection(t)}
          <HelpShortcutsSection t={t} />
          {IS_ONBOARDING_WELCOME_ROUTE_ENABLED
            ? renderHelpRestartSection(t, handleRestartRequest)
            : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>{t('help.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={restartConfirmOpen}
        onClose={handleRestartCancel}
        aria-labelledby={`${titleId}-restart`}
      >
        <DialogTitle id={`${titleId}-restart`} sx={{ fontWeight: 700 }}>
          {t('help.restartIntro.title')}
        </DialogTitle>
        <DialogContent>
          {t('help.restartIntro.body')}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button color="inherit" onClick={handleRestartCancel}>
            {t('help.restartIntro.cancel')}
          </Button>
          <Button variant="contained" disableElevation onClick={handleRestartConfirm}>
            {t('help.restartIntro.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
